import { Timestamp } from 'firebase/firestore';
import { SavedTrip } from '../types/trip';
import { GenerateSummaryResponse } from '../types/tripSummary';
import { QuoteFormData } from '../types/quote';
import { apiClient } from '../utils/apiClient';

export const tripService = {
  /**
   * Save trip (create or update)
   * @param userId - User ID
   * @param tripData - Trip data to save
   * @returns Trip ID
   */
  async saveTrip(userId: string, tripData: SavedTrip): Promise<string> {
    
    try {
      const response = await apiClient.post(`/api/advanced-trip/save-trip`, { userId, tripData });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save trip');
      }
      
      const result = await response.json();
      return result.tripId;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Load trip by ID
   * @param userId - User ID
   * @param tripId - Trip ID
   * @returns Trip data or null if not found
   */
  async loadTrip(userId: string, tripId: string): Promise<SavedTrip | null> {
    try {
      const response = await apiClient.get(`/api/advanced-trip/load-trip/${userId}/${tripId}`);
      
      if (response.status === 404) {
        return null;
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load trip');
      }
      
      const tripData = await response.json();
      
      // Convert timestamp objects back to Timestamp objects
      if (tripData.createdAt && (tripData.createdAt._seconds || tripData.createdAt.seconds)) {
        const seconds = tripData.createdAt._seconds || tripData.createdAt.seconds;
        const nanoseconds = tripData.createdAt._nanoseconds || tripData.createdAt.nanoseconds || 0;
        tripData.createdAt = new Timestamp(seconds, nanoseconds);
      }
      if (tripData.updatedAt && (tripData.updatedAt._seconds || tripData.updatedAt.seconds)) {
        const seconds = tripData.updatedAt._seconds || tripData.updatedAt.seconds;
        const nanoseconds = tripData.updatedAt._nanoseconds || tripData.updatedAt.nanoseconds || 0;
        tripData.updatedAt = new Timestamp(seconds, nanoseconds);
      }
      
      // Convert multiDayTrip dates
      if (tripData.multiDayTrip) {
        if (tripData.multiDayTrip.startDate && (tripData.multiDayTrip.startDate._seconds || tripData.multiDayTrip.startDate.seconds)) {
          const seconds = tripData.multiDayTrip.startDate._seconds || tripData.multiDayTrip.startDate.seconds;
          const nanoseconds = tripData.multiDayTrip.startDate._nanoseconds || tripData.multiDayTrip.startDate.nanoseconds || 0;
          tripData.multiDayTrip.startDate = new Timestamp(seconds, nanoseconds);
        }
        if (tripData.multiDayTrip.endDate && (tripData.multiDayTrip.endDate._seconds || tripData.multiDayTrip.endDate.seconds)) {
          const seconds = tripData.multiDayTrip.endDate._seconds || tripData.multiDayTrip.endDate.seconds;
          const nanoseconds = tripData.multiDayTrip.endDate._nanoseconds || tripData.multiDayTrip.endDate.nanoseconds || 0;
          tripData.multiDayTrip.endDate = new Timestamp(seconds, nanoseconds);
        }
      }
      
      return tripData as SavedTrip;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Get all user trips
   * @param userId - User ID
   * @returns Array of all user trips
   */
  async getUserTrips(userId: string): Promise<SavedTrip[]> {
    try {
      const response = await apiClient.get(`/api/advanced-trip/user-trips/${userId}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user trips');
      }
      
      const trips = await response.json();
      
      // Convert timestamp objects back to Timestamp objects
      return trips.map((trip: any) => {
        // Convert createdAt/updatedAt
        if (trip.createdAt && (trip.createdAt._seconds || trip.createdAt.seconds)) {
          const seconds = trip.createdAt._seconds || trip.createdAt.seconds;
          const nanoseconds = trip.createdAt._nanoseconds || trip.createdAt.nanoseconds || 0;
          trip.createdAt = new Timestamp(seconds, nanoseconds);
        }
        if (trip.updatedAt && (trip.updatedAt._seconds || trip.updatedAt.seconds)) {
          const seconds = trip.updatedAt._seconds || trip.updatedAt.seconds;
          const nanoseconds = trip.updatedAt._nanoseconds || trip.updatedAt.nanoseconds || 0;
          trip.updatedAt = new Timestamp(seconds, nanoseconds);
        }
        
        // Convert multiDayTrip dates
        if (trip.multiDayTrip) {
          if (trip.multiDayTrip.startDate && (trip.multiDayTrip.startDate._seconds || trip.multiDayTrip.startDate.seconds)) {
            const seconds = trip.multiDayTrip.startDate._seconds || trip.multiDayTrip.startDate.seconds;
            const nanoseconds = trip.multiDayTrip.startDate._nanoseconds || trip.multiDayTrip.startDate.nanoseconds || 0;
            trip.multiDayTrip.startDate = new Timestamp(seconds, nanoseconds);
          }
          if (trip.multiDayTrip.endDate && (trip.multiDayTrip.endDate._seconds || trip.multiDayTrip.endDate.seconds)) {
            const seconds = trip.multiDayTrip.endDate._seconds || trip.multiDayTrip.endDate.seconds;
            const nanoseconds = trip.multiDayTrip.endDate._nanoseconds || trip.multiDayTrip.endDate.nanoseconds || 0;
            trip.multiDayTrip.endDate = new Timestamp(seconds, nanoseconds);
          }
        }
        
        return trip as SavedTrip;
      });
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Delete trip
   * @param userId - User ID
   * @param tripId - Trip ID to delete
   */
  async deleteTrip(userId: string, tripId: string): Promise<void> {
    try {
      const response = await apiClient.delete(`/api/advanced-trip/delete-trip/${userId}/${tripId}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete trip');
      }
      
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Generate AI-powered trip summary
   * @param userId - User ID
   * @param tripId - Trip ID
   * @param forceRegenerate - Force regeneration even if cached
   * @returns Trip summary with metadata
   */
  async generateTripSummary(
    userId: string,
    tripId: string,
    forceRegenerate: boolean = false,
    optimizeOrder?: boolean
  ): Promise<GenerateSummaryResponse> {
    try {
      
      const response = await apiClient.post(`/api/advanced-trip/generate-summary`, { userId, tripId, forceRegenerate, optimizeOrder });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate trip summary');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Request a quote for a trip
   * @param userId - User ID
   * @param tripId - Trip ID
   * @param quoteData - Quote form data
   * @returns Success status and optional deep link for WhatsApp/Telegram
   */
  async requestQuote(
    userId: string,
    tripId: string,
    quoteData: QuoteFormData
  ): Promise<{
    success: boolean;
    deepLink?: string;
    message: string;
  }> {
    try {
      
      const response = await apiClient.post(`/api/advanced-trip/request-quote`, { userId, tripId, quoteData });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to request quote');
      }
      
      const data = await response.json();
      
      // Open deep link if provided (WhatsApp/Telegram)
      if (data.deepLink) {
        window.open(data.deepLink, '_blank');
      }
      
      return data;
    } catch (error: any) {
      throw error;
    }
  }
};

